/**
 * 阶段进度组件 - 垂直时间线样式
 * 显示8个生成阶段，每个阶段展示实际产物摘要
 */
import React, { useState } from 'react'
import { Tag, Button, Space, Tooltip, Card, Alert, Typography, List, Descriptions } from 'antd'
import {
  CheckCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  RedoOutlined,
  FileTextOutlined,
  TeamOutlined,
  ExperimentOutlined,
  GiftOutlined,
  PictureOutlined,
  GlobalOutlined,
  CrownOutlined,
  SafetyOutlined,
} from '@ant-design/icons'
import type { PhaseResult, AICallLog } from '@/types/scriptGeneration'

const { Text, Paragraph } = Typography

/**
 * v4 阶段配置（8阶段）
 */
const V4_PHASE_CONFIG = [
  { key: 1, name: '文本预处理', icon: <FileTextOutlined /> },
  { key: 2, name: '剧本元信息', icon: <CrownOutlined /> },
  { key: 3, name: '世界观与派系', icon: <GlobalOutlined /> },
  { key: 4, name: 'NPC与对话', icon: <TeamOutlined /> },
  { key: 5, name: '任务与时间线', icon: <ExperimentOutlined /> },
  { key: 6, name: '物品与变量', icon: <GiftOutlined /> },
  { key: 7, name: '资源描述', icon: <PictureOutlined /> },
  { key: 8, name: '验证与保存', icon: <SafetyOutlined /> },
]

/**
 * v5 Template配置（11阶段）
 */
const V5_TEMPLATE_CONFIG = [
  { key: 1, templateId: 'T1', name: 'T1: 世界圣经', icon: <FileTextOutlined /> },
  { key: 2, templateId: 'T7', name: 'T7: 游戏流程', icon: <ExperimentOutlined /> },
  { key: 3, templateId: 'T4', name: 'T4: 公共事件', icon: <GlobalOutlined /> },
  { key: 4, templateId: 'T5', name: 'T5: 人物小传', icon: <TeamOutlined /> },
  { key: 5, templateId: 'T6', name: 'T6: 交集点', icon: <GiftOutlined /> },
  { key: 6, templateId: 'T3', name: 'T3: 关系网络', icon: <TeamOutlined /> },
  { key: 7, templateId: 'T2', name: 'T2: 个人故事', icon: <CrownOutlined /> },
  { key: 8, templateId: 'T9', name: 'T9: 演员简报', icon: <PictureOutlined /> },
  { key: 9, templateId: 'T8', name: 'T8: 多结局', icon: <SafetyOutlined /> },
  { key: 10, templateId: 'T10', name: 'T10: 散场互动', icon: <GiftOutlined /> },
  { key: 11, templateId: 'T11', name: 'T11: DM手册', icon: <FileTextOutlined /> },
]

/**
 * 获取阶段配置（根据版本自适应）
 * @param version - 任务版本 '4.0' | '5.0' | undefined
 * @returns 阶段配置数组
 */
const getPhaseConfig = (version?: string) => {
  if (version === '5.0') {
    return V5_TEMPLATE_CONFIG
  }
  return V4_PHASE_CONFIG
}

/**
 * 获取阶段总数（根据版本）
 */
const getTotalPhases = (version?: string): number => {
  return version === '5.0' ? 11 : 8
}

interface PhaseProgressProps {
  currentPhase: number
  progress: number
  phaseResults: PhaseResult[]
  aiCallLogs?: AICallLog[]
  taskStatus?: string // 任务整体状态，用于判断是否可重试
  version?: string // 任务版本 '4.0' | '5.0'
  onRetryPhase?: (phase: number) => void
  onRetryAll?: () => void
}

/**
 * 从 AI 调用日志中获取指定阶段的响应内容
 * 支持 v4 (phase1_xxx) 和 v5 (T1:xxx) 两种格式
 */
const getPhaseResponseContent = (phase: number, logs: AICallLog[] = []): Record<string, unknown> | null => {
  const phaseLog = logs.find((log) => {
    // v4 格式: "phase4_npc_generation" -> 提取数字 4
    const phaseMatch = log.phase?.match(/phase(\d+)/)
    if (phaseMatch) {
      const logPhase = parseInt(phaseMatch[1], 10)
      return logPhase === phase && log.response_data?.content
    }
    // v5 格式: "T1:t1_world_bible" -> 提取数字 1
    // T1-T7 对应 phase 1-7，T8-T11 对应 phase 8
    const v5Match = log.phase?.match(/T(\d+)/)
    if (v5Match) {
      const templateNum = parseInt(v5Match[1], 10)
      // T1-T7 对应 phase 1-7，T8-T11 都对应 phase 8
      const logPhase = templateNum <= 7 ? templateNum : 8
      return logPhase === phase && log.response_data?.content
    }
    return false
  })
  return phaseLog?.response_data?.content as Record<string, unknown> | null
}

/**
 * 从产物数据中提取摘要信息
 */
const getPhaseSummary = (phase: number, data: Record<string, unknown>): React.ReactNode => {
  // 处理可能的嵌套数据结构
  const displayData = (data.data as Record<string, unknown>) || data

  switch (phase) {
    case 1: {
      // Phase 1: 文本预处理 - 显示标题、摘要、统计信息
      // v4: structured_text, v5: era/world_setting
      const structuredText = displayData.structured_text as Record<string, unknown> | undefined
      const era = displayData.era as string | undefined

      // v5 T1 格式
      if (era) {
        const spaces = displayData.spaces as Array<{ id?: string; name?: string }> | undefined
        const worldNpcs = displayData.world_npcs as Array<{ id?: string; name?: string }> | undefined
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text strong style={{ fontSize: 13 }}>{era}</Text>
            <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ margin: 0, fontSize: 12 }}>
              {displayData.world_setting as string || ''}
            </Paragraph>
            <Space size={2} wrap>
              <Tag color="blue">空间 {spaces?.length || 0}</Tag>
              <Tag color="green">NPC {worldNpcs?.length || 0}</Tag>
            </Space>
          </Space>
        )
      }

      // v4 格式
      if (structuredText) {
        const title = structuredText.title as string
        const summary = structuredText.summary as string
        const paragraphCount = (structuredText.paragraphs as string[])?.length || 0
        const sceneCount = (structuredText.scenes as string[])?.length || 0
        const dialogueCount = (structuredText.dialogues as Array<{ speaker: string; content: string }>)?.length || 0
        const characterCount = (structuredText.characters as string[])?.length || 0
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {title && <Text strong style={{ fontSize: 13 }}>{title}</Text>}
            {summary && (
              <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ margin: 0, fontSize: 12 }}>
                {summary}
              </Paragraph>
            )}
            <Space size={2} wrap>
              <Tag color="blue">段落 {paragraphCount}</Tag>
              <Tag color="green">场景 {sceneCount}</Tag>
              <Tag color="purple">对话 {dialogueCount}</Tag>
              <Tag>角色 {characterCount}</Tag>
            </Space>
          </Space>
        )
      }
      return <Text type="secondary">已提取文本结构</Text>
    }

    case 2: {
      // Phase 2: 剧本元信息 - 显示标题、类型、难度、主题统计
      // v4: metadata, v5: phases/timeline
      const metadata = displayData.metadata as Record<string, unknown> | undefined
      const phases = displayData.phases as Array<{ name?: string; type?: string }> | undefined
      const timeline = displayData.timeline as Array<{ time?: string; event?: string }> | undefined

      // v5 T7 格式
      if (phases || timeline) {
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Space wrap size={4}>
              <Text strong>游戏流程</Text>
              <Tag color="blue">{phases?.length || 0} 个阶段</Tag>
              <Tag color="green">{timeline?.length || 0} 个时间点</Tag>
            </Space>
          </Space>
        )
      }

      // v4 格式
      if (metadata) {
        const title = metadata.title as string
        const genre = metadata.genre as string
        const difficulty = metadata.difficulty as string
        const duration = metadata.estimated_duration as string
        const themeCount = (metadata.theme as string[])?.length || 0
        const keyPlotCount = (metadata.key_plot_points as string[])?.length || 0
        const requiredCount = (metadata.required_elements as string[])?.length || 0
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Space wrap size={4}>
              {title && <Text strong>{title}</Text>}
              {genre && <Tag color="purple">{genre}</Tag>}
              {difficulty && <Tag color={difficulty === '困难' ? 'red' : 'blue'}>{difficulty}</Tag>}
              {duration && <Text type="secondary">{duration}</Text>}
            </Space>
            <Space size={2} wrap>
              <Tag color="cyan">主题 {themeCount}</Tag>
              <Tag color="orange">关键点 {keyPlotCount}</Tag>
              {requiredCount > 0 && <Tag color="gold">必需元素 {requiredCount}</Tag>}
            </Space>
          </Space>
        )
      }
      return <Text type="secondary">已生成剧本元信息</Text>
    }

    case 3: {
      // Phase 3: 世界观与派系 - 显示世界观名称、派系数量、规则限制统计
      // v4: world/factions, v5: events
      const world = displayData.world as Record<string, unknown> | undefined
      const factions = displayData.factions as unknown[] | undefined
      const events = displayData.events as Array<{ name?: string; phase?: string }> | undefined

      // v5 T4 格式
      if (events && events.length > 0) {
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Space wrap size={4}>
              <Text strong>公共事件</Text>
              <Tag color="blue">{events.length} 个事件</Tag>
            </Space>
            <Space wrap size={2}>
              {events.slice(0, 4).map((e, idx) => (
                <Tag key={idx} color="green">{e.name}</Tag>
              ))}
              {events.length > 4 && <Tag>+{events.length - 4}</Tag>}
            </Space>
          </Space>
        )
      }

      // v4 格式
      if (world) {
        const name = world.name as string
        const era = world.era as string
        const location = world.location as string
        const factionCount = factions?.length || 0
        const ruleCount = (world.core_rules as string[])?.length || 0
        const restrictionCount = (world.restrictions as string[])?.length || 0
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Space wrap size={4}>
              <Text strong>{name}</Text>
              {era && <Tag>{era}</Tag>}
              {location && <Text type="secondary">{location}</Text>}
            </Space>
            <Space size={2} wrap>
              <Tag color="blue">派系 {factionCount}</Tag>
              <Tag>规则 {ruleCount}</Tag>
              {restrictionCount > 0 && <Tag color="red">限制 {restrictionCount}</Tag>}
            </Space>
          </Space>
        )
      }
      return <Text type="secondary">已生成世界观与派系</Text>
    }

    case 4: {
      // Phase 4: NPC与对话 - 显示NPC数量和名称列表、对话模板统计
      // v4: npcs, v5: player_biographies/actor_biographies
      const npcs = displayData.npcs as unknown[] | undefined
      const playerBiographies = displayData.player_biographies as Array<{ name?: string }> | undefined
      const actorBiographies = displayData.actor_biographies as Array<{ name?: string }> | undefined

      // v5 T5 格式
      if (playerBiographies || actorBiographies) {
        const totalCount = (playerBiographies?.length || 0) + (actorBiographies?.length || 0)
        const allNames = [
          ...(playerBiographies || []).map(p => p.name),
          ...(actorBiographies || []).map(a => a.name),
        ].filter(Boolean)
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text strong>人物角色 ({totalCount} 个)</Text>
            <Space wrap size={2}>
              {allNames.slice(0, 4).map((name, idx) => (
                <Tag key={idx} color="blue">{name}</Tag>
              ))}
              {allNames.length > 4 && <Tag>+{allNames.length - 4}</Tag>}
            </Space>
          </Space>
        )
      }

      // v4 格式
      if (npcs && npcs.length > 0) {
        const names = npcs.map((n) => (n as { name?: string }).name).filter(Boolean)
        const templateTypes = new Set<string>()
        npcs.forEach((n) => {
          const templates = (n as { dialogue_templates?: Record<string, string[]> })?.dialogue_templates
          if (templates) {
            Object.keys(templates).forEach((k) => templateTypes.add(k))
          }
        })
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text strong>NPC ({npcs.length} 个)</Text>
            <Space wrap size={2}>
              {names.slice(0, 4).map((name, idx) => (
                <Tag key={idx} color="blue">{name}</Tag>
              ))}
              {names.length > 4 && <Tag>+{names.length - 4}</Tag>}
            </Space>
            {templateTypes.size > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                对话模板: {Array.from(templateTypes).join(', ')}
              </Text>
            )}
          </Space>
        )
      }
      return <Text type="secondary">已生成 NPC 与对话</Text>
    }

    case 5: {
      // Phase 5: 任务与时间线 - 显示任务数量、时间线数量
      // v4: quests/timeline, v5: intersection_points
      const quests = displayData.quests as unknown[] | undefined
      const timeline = displayData.timeline as unknown[] | undefined
      const intersectionPoints = displayData.intersection_points as Array<{ scene?: string }> | undefined

      // v5 T6 格式
      if (intersectionPoints && intersectionPoints.length > 0) {
        return (
          <Space wrap size={4}>
            <Tag color="green">交集点 {intersectionPoints.length}</Tag>
          </Space>
        )
      }

      // v4 格式
      const questCount = quests?.length || 0
      const timelineCount = timeline?.length || 0
      if (questCount > 0 || timelineCount > 0) {
        return (
          <Space wrap size={4}>
            <Tag color="green">任务 {questCount}</Tag>
            <Tag color="cyan">时间线 {timelineCount}</Tag>
          </Space>
        )
      }
      return <Text type="secondary">已生成任务与时间线</Text>
    }

    case 6: {
      // Phase 6: 物品与变量 - 显示物品数量、变量数量
      // v4: items/variables, v5: relations
      const items = displayData.items as unknown[] | undefined
      const variables = displayData.variables as unknown[] | undefined
      const relations = displayData.relations as Array<{ from?: string; to?: string }> | undefined

      // v5 T3 格式
      if (relations && relations.length > 0) {
        return (
          <Space wrap size={4}>
            <Tag color="purple">关系 {relations.length}</Tag>
          </Space>
        )
      }

      // v4 格式
      const itemCount = items?.length || 0
      const varCount = variables?.length || 0
      if (itemCount > 0 || varCount > 0) {
        return (
          <Space wrap size={4}>
            <Tag color="gold">物品 {itemCount}</Tag>
            <Tag color="purple">变量 {varCount}</Tag>
          </Space>
        )
      }
      return <Text type="secondary">已生成物品与变量</Text>
    }

    case 7: {
      // Phase 7: 资源描述 - 显示NPC头像数量、场景背景数量
      const resources = displayData.resources as Record<string, unknown> | undefined
      if (resources) {
        const npcImages = (resources.npc_images as unknown[])?.length || 0
        const sceneImages = (resources.scene_images as unknown[])?.length || 0
        return (
          <Space wrap size={4}>
            <Tag color="pink">NPC头像 {npcImages}</Tag>
            <Tag color="cyan">场景背景 {sceneImages}</Tag>
          </Space>
        )
      }
      return <Text type="secondary">已生成资源描述</Text>
    }

    case 8: {
      // Phase 8: 验证与保存 - 显示验证通过
      return <Text type="success">✓ 配置已验证并保存</Text>
    }

    default:
      return null
  }
}

/**
 * 获取阶段摘要文本（用于Tooltip）
 */
const getPhaseSummaryText = (phase: number, data: Record<string, unknown>): string => {
  // 处理可能的嵌套数据结构
  const displayData = (data.data as Record<string, unknown>) || data

  switch (phase) {
    case 1: {
      // v4: structured_text, v5 T1: era/world_setting/spaces
      const st = displayData.structured_text as Record<string, unknown> | undefined
      if (st?.title) return st.title as string
      // v5 T1 世界圣经
      const era = displayData.era as string
      if (era) return era.substring(0, 30) + (era.length > 30 ? '...' : '')
      return '文本预处理'
    }
    case 2: {
      // v4: metadata, v5 T7: phases/timeline
      const m = displayData.metadata as Record<string, unknown> | undefined
      if (m?.title) return m.title as string
      // v5 T7 游戏流程
      const phases = displayData.phases as unknown[] | undefined
      if (phases?.length) return `${phases.length}个游戏阶段`
      return '剧本元信息'
    }
    case 3: {
      // v4: world, v5 T4: events
      const w = displayData.world as Record<string, unknown> | undefined
      if (w?.name) return w.name as string
      // v5 T4 公共事件
      const events = displayData.events as unknown[] | undefined
      if (events?.length) return `${events.length}个公共事件`
      return '世界观'
    }
    case 4: {
      // v4: npcs, v5 T5: player_biographies/actor_biographies
      const npcs = displayData.npcs as unknown[] | undefined
      if (npcs?.length) return npcs.map((n) => (n as { name: string }).name).join(', ')
      // v5 T5 人物小传
      const players = displayData.player_biographies as unknown[] | undefined
      const actors = displayData.actor_biographies as unknown[] | undefined
      const total = (players?.length || 0) + (actors?.length || 0)
      if (total > 0) return `${total}个人物角色`
      return 'NPC列表'
    }
    case 5: {
      // v4: quests, v5 T6: intersection_points
      const quests = displayData.quests as unknown[] | undefined
      if (quests?.length) return quests.map((q) => (q as { name: string }).name).join(', ')
      // v5 T6 交集点
      const intersections = displayData.intersection_points as unknown[] | undefined
      if (intersections?.length) return `${intersections.length}个交集场景`
      return '任务列表'
    }
    case 6: {
      // v4: items, v5 T3: relations (关系网络)
      const items = displayData.items as unknown[] | undefined
      if (items?.length) return items.map((i) => (i as { name: string }).name).join(', ')
      // v5 T3 关系网络
      const relations = displayData.relations as unknown[] | undefined
      if (relations?.length) return `${relations.length}个关系定义`
      return '物品列表'
    }
    default:
      return ''
  }
}

const PhaseProgress: React.FC<PhaseProgressProps> = ({
  currentPhase,
  phaseResults,
  aiCallLogs = [],
  taskStatus,
  version,
  onRetryPhase,
  onRetryAll,
}) => {
  const [expandedPhases, setExpandedPhases] = useState<number[]>([])

  // 获取当前版本的阶段配置
  const phaseConfig = getPhaseConfig(version)
  const totalPhases = getTotalPhases(version)
  const isV5 = version === '5.0'

  // 获取阶段状态
  const getPhaseStatus = (phaseKey: number): 'completed' | 'running' | 'failed' | 'pending' => {
    const log = phaseResults.find((l) => l.phase === phaseKey)
    if (!log) {
      // 如果当前阶段还没有结果记录
      if (phaseKey < currentPhase) return 'completed' // 前面已完成的阶段
      if (phaseKey === currentPhase) {
        // 如果任务已失败，当前阶段也应该显示为失败（允许重试）
        if (taskStatus === 'failed') return 'failed'
        return 'running'  // 正在执行的阶段
      }
      return 'pending' // 还未开始的阶段
    }
    // 支持 v4 (success: boolean) 和 v5 (status: string)
    if (log.status) {
      return log.status === 'completed' ? 'completed' : log.status === 'failed' ? 'failed' : 'running'
    }
    return log.success ? 'completed' : 'failed'
  }

  // 获取状态图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
      case 'running':
        return <LoadingOutlined style={{ color: '#1890ff', fontSize: 18 }} />
      case 'failed':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
      default:
        return <ClockCircleOutlined style={{ color: '#d9d9d9', fontSize: 18 }} />
    }
  }

  // 获取状态标签
  const getStatusTag = (status: string) => {
    switch (status) {
      case 'completed':
        return <Tag color="success">完成</Tag>
      case 'running':
        return <Tag color="processing">进行中</Tag>
      case 'failed':
        return <Tag color="error">失败</Tag>
      default:
        return <Tag color="default">等待</Tag>
    }
  }

  // 切换阶段展开状态
  const togglePhase = (phaseKey: number) => {
    setExpandedPhases((prev) => (prev.includes(phaseKey) ? [] : [phaseKey]))
  }

  // 获取阶段日志
  const getPhaseLog = (phaseKey: number) => {
    return phaseResults.find((l) => l.phase === phaseKey)
  }

  return (
    <div style={{ display: 'flex', gap: 24, minHeight: 500 }}>
      {/* 左侧：垂直时间线 */}
      <div style={{ width: 340, flexShrink: 0 }}>
        <Card
          title={
            <Space>
              <span>生成阶段</span>
              <Text type="secondary" style={{ fontWeight: 'normal' }}>
                ({phaseResults.filter((p) => p.success || p.status === 'completed').length}/{phaseConfig.length})
              </Text>
              {onRetryAll && (
                <Tooltip title="从头开始重试">
                  <Button
                    type="text"
                    icon={<RedoOutlined />}
                    size="small"
                    onClick={onRetryAll}
                  />
                </Tooltip>
              )}
            </Space>
          }
          size="small"
          styles={{ body: { padding: '12px 0' } }}
        >
          <div style={{ position: 'relative', paddingLeft: 24 }}>
            {/* 时间线主线 */}
            <div
              style={{
                position: 'absolute',
                left: 27,
                top: 10,
                bottom: 10,
                width: 2,
                background: 'linear-gradient(to bottom, #52c41a 0%, #1890ff 50%, #d9d9d9 100%)',
                borderRadius: 1,
              }}
            />
            {phaseConfig.map((phase, index) => {
              const status = getPhaseStatus(phase.key)
              const isLast = index === phaseConfig.length - 1
              const log = getPhaseLog(phase.key)
              const hasData = log?.data && Object.keys(log.data).length > 0

              return (
                <div
                  key={phase.key}
                  onClick={() => togglePhase(phase.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '10px 8px',
                    marginBottom: isLast ? 0 : 2,
                    cursor: 'pointer',
                    borderRadius: 6,
                    transition: 'background 0.2s',
                    background: expandedPhases.includes(phase.key) ? '#e6f7ff' : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!expandedPhases.includes(phase.key)) {
                      e.currentTarget.style.background = '#f5f5f5'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedPhases.includes(phase.key)) {
                      e.currentTarget.style.background = 'transparent'
                    }
                  }}
                >
                  {/* 状态图标 */}
                  <div style={{ marginRight: 10, marginTop: 2, zIndex: 1 }}>
                    {getStatusIcon(status)}
                  </div>

                  {/* 阶段信息 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Space align="center" style={{ marginBottom: 4 }}>
                      {phase.icon}
                      <span style={{ fontWeight: 600, fontSize: 14 }}>
                        {phase.key}. {phase.name}
                      </span>
                      {getStatusTag(status)}
                    </Space>

                    {/* 实际产物摘要 - 替代固定描述 */}
                    {hasData ? (
                      <Tooltip title={getPhaseSummaryText(phase.key, log.data as Record<string, unknown>)}>
                        <div style={{ fontSize: 12 }}>
                          {getPhaseSummary(phase.key, log.data as Record<string, unknown>)}
                        </div>
                      </Tooltip>
                    ) : status === 'pending' ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        等待执行...
                      </Text>
                    ) : status === 'running' ? (
                      <Text style={{ fontSize: 12, color: '#1890ff' }}>
                        正在生成...
                      </Text>
                    ) : null}

                    {/* 错误信息 - 支持 v4 (error) 和 v5 (error_message) */}
                    {(log?.error || log?.error_message) && (
                      <div style={{ fontSize: 11, color: '#ff4d4f', marginTop: 4 }}>
                        ⚠ {(log.error || log.error_message).substring(0, 50)}...
                      </div>
                    )}
                  </div>

                  {/* 操作按钮 */}
                  <div style={{ marginLeft: 4 }} onClick={(e) => e.stopPropagation()}>
                    <Space size={2}>
                      {hasData && (
                        <Tooltip title="查看详情">
                          <Button
                            type="text"
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => togglePhase(phase.key)}
                          />
                        </Tooltip>
                      )}
                      {(status === 'failed' || status === 'completed') && onRetryPhase && (
                        <Tooltip title="重试">
                          <Button
                            type="text"
                            icon={<RedoOutlined />}
                            size="small"
                            onClick={() => onRetryPhase(phase.key)}
                          />
                        </Tooltip>
                      )}
                    </Space>
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      </div>

      {/* 右侧：详情面板 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {expandedPhases.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {expandedPhases.map((phaseKey) => {
              const phase = phaseConfig.find((p) => p.key === phaseKey)!
              const log = getPhaseLog(phaseKey)
              const status = getPhaseStatus(phaseKey)
              const hasData = log?.data && Object.keys(log.data).length > 0

              return (
                <Card
                  key={phaseKey}
                  title={
                    <Space>
                      {phase.icon}
                      <span>{phase.key}. {phase.name}</span>
                      {getStatusTag(status)}
                    </Space>
                  }
                  size="small"
                  extra={
                    <Button type="text" size="small" onClick={() => setExpandedPhases([])}>
                      收起
                    </Button>
                  }
                >
                  {/* 统计信息 */}
                  {log && (
                    <Descriptions column={4} size="small" style={{ marginBottom: 16 }}>
                      <Descriptions.Item label="AI 调用">{log.ai_calls} 次</Descriptions.Item>
                      <Descriptions.Item label="Token">{log.tokens}</Descriptions.Item>
                      <Descriptions.Item label="成本">¥{log.cost_yuan?.toFixed(4) || '0'}</Descriptions.Item>
                      <Descriptions.Item label="耗时">{log.duration_ms}ms</Descriptions.Item>
                    </Descriptions>
                  )}

                  {/* 完成时间 */}
                  {log?.completed_at && (
                    <div style={{ marginBottom: 16 }}>
                      <Text type="secondary">完成时间: {new Date(log.completed_at).toLocaleString('zh-CN')}</Text>
                    </div>
                  )}

                  {/* 错误信息 - 支持 v4 (error) 和 v5 (error_message) */}
                  {(log?.error || log?.error_message) && (
                    <Alert
                      message="执行失败"
                      description={log.error || log.error_message}
                      type="error"
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {/* 产物数据展示 */}
                  {hasData ? (
                    <Card
                      size="small"
                      title="阶段产物"
                      styles={{ body: { padding: 0 } }}
                    >
                      <PhaseDataViewer
                        phase={phaseKey}
                        data={log?.data as Record<string, unknown>}
                        aiContent={getPhaseResponseContent(phaseKey, aiCallLogs)}
                      />
                    </Card>
                  ) : status === 'pending' ? (
                    <Alert message="该阶段尚未执行" type="info" showIcon />
                  ) : status === 'running' ? (
                    <Alert message="该阶段正在执行中" type="info" showIcon />
                  ) : (log?.error || log?.error_message) ? (
                    // 失败时显示错误信息，而不是"无产物数据"
                    <Alert
                      message="执行失败"
                      description={log.error || log.error_message}
                      type="error"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  ) : (
                    <Alert message="无产物数据" type="warning" showIcon />
                  )}
                </Card>
              )
            })}
          </div>
        ) : (
          <Card
            style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div style={{ textAlign: 'center', color: '#999' }}>
              <EyeOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <div>点击左侧阶段查看详情</div>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

/**
 * 阶段数据展示组件
 * 优先从 AI 调用日志获取实际内容，否则使用 phaseResults.data
 */
const PhaseDataViewer: React.FC<{ phase: number; data: Record<string, unknown>; aiContent?: Record<string, unknown> | null }> = ({
  phase,
  data,
  aiContent,
}) => {
  // 优先使用 AI 实际生成的内容
  const rawData = aiContent || data || {}

  // 处理可能的嵌套数据结构（如 data.data.era）
  const displayData = (rawData.data as Record<string, unknown>) || rawData

  // 调试日志（仅在开发环境显示）
  if (process.env.NODE_ENV === 'development') {
    console.log(`[PhaseDataViewer] Phase ${phase}:`, {
      rawDataKeys: Object.keys(rawData),
      displayDataKeys: Object.keys(displayData),
      hasEra: !!displayData.era,
      hasSpaces: Array.isArray(displayData.spaces),
    })
  }

  switch (phase) {
    case 1: {
      // v4: structured_text, v5 T1: era/world_setting/spaces/world_npcs
      const st = displayData.structured_text as Record<string, unknown> | undefined
      const era = displayData.era as string | undefined

      // v5 T1 世界圣经格式
      if (era) {
        const spaces = displayData.spaces as Array<{ id: string; name: string; type: string; description: string }> | undefined
        const worldNpcs = displayData.world_npcs as Array<{ id: string; name: string; role: string; description: string }> | undefined
        return (
          <div style={{ padding: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="时代">{era}</Descriptions.Item>
              <Descriptions.Item label="世界设定">{displayData.world_setting as string}</Descriptions.Item>
              <Descriptions.Item label="核心真相">{displayData.core_truth as string}</Descriptions.Item>
            </Descriptions>
            {/* 空间列表 */}
            {spaces && spaces.length > 0 && (
              <Card size="small" title={`空间列表 (${spaces.length})`} style={{ marginTop: 16 }}>
                <List
                  size="small"
                  dataSource={spaces}
                  renderItem={(space) => (
                    <List.Item>
                      <Descriptions column={1} size="small" style={{ width: '100%' }}>
                        <Descriptions.Item label="名称">
                          <Tag color={space.type === 'public' ? 'blue' : 'orange'}>{space.type === 'public' ? '公共' : '私密'}</Tag>
                          {space.name}
                        </Descriptions.Item>
                        <Descriptions.Item label="描述">{space.description}</Descriptions.Item>
                      </Descriptions>
                    </List.Item>
                  )}
                />
              </Card>
            )}
            {/* 世界NPC列表 */}
            {worldNpcs && worldNpcs.length > 0 && (
              <Card size="small" title={`世界NPC (${worldNpcs.length})`} style={{ marginTop: 16 }}>
                <List
                  size="small"
                  dataSource={worldNpcs}
                  renderItem={(npc) => (
                    <List.Item>
                      <Descriptions column={1} size="small" style={{ width: '100%' }}>
                        <Descriptions.Item label="名称">{npc.name}</Descriptions.Item>
                        <Descriptions.Item label="角色">{npc.role}</Descriptions.Item>
                        <Descriptions.Item label="描述">{npc.description}</Descriptions.Item>
                      </Descriptions>
                    </List.Item>
                  )}
                />
              </Card>
            )}
          </div>
        )
      }

      // v4 格式
      if (!st) return <Alert message="无结构化文本数据" type="warning" />
      return (
        <div style={{ padding: 16 }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="标题">{st.title as string}</Descriptions.Item>
            <Descriptions.Item label="摘要">{st.summary as string}</Descriptions.Item>
          </Descriptions>

          {/* 段落 */}
          {(st.paragraphs as string[])?.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>段落 ({(st.paragraphs as string[]).length})</Text>
              <List
                size="small"
                dataSource={st.paragraphs as string[]}
                renderItem={(p, idx) => (
                  <List.Item>
                    <Text style={{ fontSize: 12 }}>{idx + 1}. {p}</Text>
                  </List.Item>
                )}
              />
            </>
          )}

          {/* 场景 */}
          {(st.scenes as string[])?.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>场景 ({(st.scenes as string[]).length})</Text>
              <Space wrap>
                {(st.scenes as string[]).map((s, idx) => (
                  <Tag key={idx} color="blue">{s}</Tag>
                ))}
              </Space>
            </>
          )}

          {/* 角色 */}
          {(st.characters as string[])?.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>角色 ({(st.characters as string[]).length})</Text>
              <Space wrap>
                {(st.characters as string[]).map((c, idx) => (
                  <Tag key={idx} color="green">{c}</Tag>
                ))}
              </Space>
            </>
          )}

          {/* 对话 */}
          {(st.dialogues as Array<{ speaker: string; content: string; emotion?: string }>)?.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>
                对话 ({(st.dialogues as Array<{ speaker: string; content: string }>)?.length || 0})
              </Text>
              <List
                size="small"
                dataSource={st.dialogues as Array<{ speaker: string; content: string; emotion?: string }>}
                renderItem={(d) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="说话人">
                        <Space>
                          <Tag color="purple">{d.speaker}</Tag>
                          {d.emotion && <Tag color="red">{d.emotion}</Tag>}
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="内容">{d.content}</Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            </>
          )}
        </div>
      )
    }

    case 2: {
      // v4: metadata, v5 T7: phases/timeline
      const m = displayData.metadata as Record<string, unknown> | undefined
      const phases = displayData.phases as Array<{ id: string; name: string; type: string; duration_minutes: number; objective: string }> | undefined
      const timeline = displayData.timeline as Array<{ time: string; event: string }> | undefined

      // v5 T7 游戏流程格式
      if (phases || timeline) {
        return (
          <div style={{ padding: 16 }}>
            {phases && phases.length > 0 && (
              <Card size="small" title={`游戏阶段 (${phases.length})`} style={{ marginBottom: 16 }}>
                <List
                  size="small"
                  dataSource={phases}
                  renderItem={(phase) => (
                    <List.Item>
                      <Descriptions column={1} size="small" style={{ width: '100%' }}>
                        <Descriptions.Item label="阶段">
                          <Tag color="blue">{phase.type}</Tag>
                          {phase.name}
                        </Descriptions.Item>
                        <Descriptions.Item label="时长">{phase.duration_minutes}分钟</Descriptions.Item>
                        <Descriptions.Item label="目标">{phase.objective}</Descriptions.Item>
                      </Descriptions>
                    </List.Item>
                  )}
                />
              </Card>
            )}
            {timeline && timeline.length > 0 && (
              <Card size="small" title={`时间线 (${timeline.length})`}>
                <List
                  size="small"
                  dataSource={timeline}
                  renderItem={(item) => (
                    <List.Item>
                      <Space>
                        <Tag color="purple">{item.time}</Tag>
                        <Text>{item.event}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </Card>
            )}
          </div>
        )
      }

      // v4 格式
      if (!m) return <Alert message="无元数据" type="warning" />
      return (
        <div style={{ padding: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="标题">{m.title as string}</Descriptions.Item>
            <Descriptions.Item label="类型">{m.genre as string}</Descriptions.Item>
            <Descriptions.Item label="难度">{m.difficulty as string}</Descriptions.Item>
            <Descriptions.Item label="时长">{m.estimated_duration as string}</Descriptions.Item>
            <Descriptions.Item label="目标受众">{m.target_audience as string}</Descriptions.Item>
            <Descriptions.Item label="核心冲突" span={2}>{m.core_conflict as string}</Descriptions.Item>
          </Descriptions>

          {/* 主题标签 */}
          {(m.theme as string[])?.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>主题标签</Text>
              <Space wrap>
                {(m.theme as string[]).map((t, idx) => (
                  <Tag key={idx} color="cyan">{t}</Tag>
                ))}
              </Space>
            </>
          )}

          {/* 关键情节点 */}
          {(m.key_plot_points as string[])?.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>关键情节点</Text>
              <List
                size="small"
                dataSource={m.key_plot_points as string[]}
                renderItem={(point, idx) => (
                  <List.Item>
                    <Text style={{ fontSize: 12 }}>{idx + 1}. {point}</Text>
                  </List.Item>
                )}
              />
            </>
          )}

          {/* 必需元素 */}
          {(m.required_elements as string[])?.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>必需元素</Text>
              <Space wrap>
                {(m.required_elements as string[]).map((el, idx) => (
                  <Tag key={idx} color="gold">{el}</Tag>
                ))}
              </Space>
            </>
          )}
        </div>
      )
    }

    case 3: {
      // v4: world/factions, v5 T4: events
      const world = displayData.world as Record<string, unknown> | undefined
      const factions = displayData.factions as Record<string, unknown>[] | undefined
      const events = displayData.events as Array<{ id: string; name: string; phase: string; description: string; purpose: string }> | undefined

      // v5 T4 公共事件格式
      if (events && events.length > 0) {
        return (
          <div style={{ padding: 16 }}>
            <Card size="small" title={`公共事件 (${events.length})`}>
              <List
                size="small"
                dataSource={events}
                renderItem={(evt) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="事件">
                        <Tag color="blue">{evt.phase}</Tag>
                        {evt.name}
                      </Descriptions.Item>
                      <Descriptions.Item label="描述">{evt.description}</Descriptions.Item>
                      <Descriptions.Item label="目的">{evt.purpose}</Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            </Card>
          </div>
        )
      }

      // v4 格式
      if (!world) return <Alert message="无世界观数据" type="warning" />
      return (
        <div style={{ padding: 16 }}>
          <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="世界观名称">{world.name as string}</Descriptions.Item>
            <Descriptions.Item label="时代">{world.era as string}</Descriptions.Item>
            <Descriptions.Item label="地点">{world.location as string}</Descriptions.Item>
            <Descriptions.Item label="描述">{world.description as string}</Descriptions.Item>
          </Descriptions>

          {/* 核心规则 */}
          {(world.core_rules as string[])?.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>核心规则</Text>
              <List
                size="small"
                dataSource={world.core_rules as string[]}
                renderItem={(rule, idx) => (
                  <List.Item>
                    <Text style={{ fontSize: 12 }}>{idx + 1}. {rule}</Text>
                  </List.Item>
                )}
              />
            </>
          )}

          {/* 限制 */}
          {(world.restrictions as string[])?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>限制</Text>
              <Space wrap>
                {(world.restrictions as string[]).map((r, idx) => (
                  <Tag key={idx} color="red">{r}</Tag>
                ))}
              </Space>
            </div>
          )}

          {/* 派系 */}
          {factions && factions.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>
                派系 ({factions.length})
              </Text>
              {factions.map((f, idx) => {
                const factionData = f as Record<string, unknown>
                const goals = factionData.goals as string[] | undefined
                return (
                  <Card key={idx} size="small" style={{ marginBottom: 8 }} title={factionData.name as string} extra={<Tag>{factionData.id as string}</Tag>}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{factionData.description as string}</Text>

                    {/* 派系目标 */}
                    {Array.isArray(goals) && goals.length > 0 ? (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>目标:</Text>
                        <List
                          size="small"
                          style={{ marginTop: 4 }}
                          dataSource={goals}
                          renderItem={(g: string) => (
                            <List.Item style={{ border: 'none', padding: '4px 0' }}>
                              <Text style={{ fontSize: 12 }}>{g}</Text>
                            </List.Item>
                          )}
                        />
                      </div>
                    ) : null}

                    {/* 派系关系 */}
                    {factionData.relationships ? (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>势力关系:</Text>
                        <Space wrap style={{ marginTop: 4 }}>
                          {Object.entries(factionData.relationships as Record<string, string>).map(([target, rel]) => (
                            <Tag key={target} color="purple">{target}: {rel}</Tag>
                          ))}
                        </Space>
                      </div>
                    ) : null}
                  </Card>
                )
              })}
            </>
          )}
        </div>
      )
    }

    case 4: {
      // v4: npcs, v5 T5: player_biographies/actor_biographies
      const npcs = displayData.npcs as Record<string, unknown>[] | undefined
      const playerBiographies = displayData.player_biographies as Array<{
        name: string
        basic_info?: { age?: number; gender?: string; occupation?: string }
        background?: string
        personality?: string
      }> | undefined
      const actorBiographies = displayData.actor_biographies as Array<{
        name: string
        basic_info?: { age?: number; gender?: string; occupation?: string }
        background?: string
      }> | undefined

      // v5 T5 人物小传格式
      if (playerBiographies || actorBiographies) {
        const players = playerBiographies || []
        const actors = actorBiographies || []
        return (
          <div style={{ padding: 16 }}>
            {players.length > 0 && (
              <Card size="small" title={`玩家角色 (${players.length})`} style={{ marginBottom: 16 }}>
                <List
                  size="small"
                  dataSource={players}
                  renderItem={(player) => (
                    <List.Item>
                      <Descriptions column={1} size="small" style={{ width: '100%' }}>
                        <Descriptions.Item label="名称">{player.name}</Descriptions.Item>
                        <Descriptions.Item label="基本信息">
                          {player.basic_info?.gender} · {player.basic_info?.age}岁 · {player.basic_info?.occupation}
                        </Descriptions.Item>
                        <Descriptions.Item label="背景">{player.background?.substring(0, 100)}...</Descriptions.Item>
                        <Descriptions.Item label="性格">{player.personality?.substring(0, 100)}...</Descriptions.Item>
                      </Descriptions>
                    </List.Item>
                  )}
                />
              </Card>
            )}
            {actors.length > 0 && (
              <Card size="small" title={`演员角色 (${actors.length})`}>
                <List
                  size="small"
                  dataSource={actors}
                  renderItem={(actor) => (
                    <List.Item>
                      <Descriptions column={1} size="small" style={{ width: '100%' }}>
                        <Descriptions.Item label="名称">{actor.name}</Descriptions.Item>
                        <Descriptions.Item label="背景">{actor.background?.substring(0, 100)}...</Descriptions.Item>
                      </Descriptions>
                    </List.Item>
                  )}
                />
              </Card>
            )}
          </div>
        )
      }

      // v4 格式
      if (!npcs) return <Alert message="无 NPC 数据" type="warning" />
      return (
        <List
          grid={{ gutter: 16, column: 2 }}
          dataSource={npcs}
          renderItem={(npc) => (
            <List.Item>
              <Card
                size="small"
                title={
                  <Space>
                    <TeamOutlined />
                    {npc.name as string}
                  </Space>
                }
                extra={<Tag color="blue">{npc.role as string}</Tag>}
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="描述">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {(npc.description as string)?.substring(0, 100)}...
                    </Text>
                  </Descriptions.Item>
                </Descriptions>

                {/* 性格 */}
                {Array.isArray(npc.personality) && (npc.personality as string[])?.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Space wrap>
                      {(npc.personality as string[]).map((p, idx) => (
                        <Tag key={idx}>{p}</Tag>
                      ))}
                    </Space>
                  </div>
                )}

                {/* 说话风格 */}
                {!!npc.speech_style && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>说话风格: </Text>
                    <Text style={{ fontSize: 12 }}>{npc.speech_style as string}</Text>
                  </div>
                )}

                {/* 可聊话题 */}
                {Array.isArray(npc.topics) && (npc.topics as string[])?.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>可聊话题:</Text>
                    <Space wrap style={{ marginTop: 4 }}>
                      {(npc.topics as string[]).map((t, idx) => (
                        <Tag key={idx} color="cyan">{t}</Tag>
                      ))}
                    </Space>
                  </div>
                )}

                {/* 对话模板 */}
                {!!npc.dialogue_templates && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ fontSize: 12 }}>对话模板:</Text>
                    <Space wrap style={{ marginTop: 4 }}>
                      {Object.entries(npc.dialogue_templates as Record<string, string[]>).map(([type, templates]) => (
                        <Tag key={type} color="green">{type} ({templates.length})</Tag>
                      ))}
                    </Space>
                  </div>
                )}

                {/* 招呼语 */}
                {Array.isArray(npc.greetings) && (npc.greetings as string[])?.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>招呼语:</Text>
                    <List
                      size="small"
                      style={{ marginTop: 4 }}
                      dataSource={npc.greetings as string[]}
                      renderItem={(g) => (
                        <List.Item style={{ border: 'none', padding: '2px 0' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>"{g}"</Text>
                        </List.Item>
                      )}
                    />
                  </div>
                )}
              </Card>
            </List.Item>
          )}
        />
      )
    }

    case 5: {
      // v4: quests/timeline, v5 T6: intersection_points
      const quests = displayData.quests as Record<string, unknown>[] | undefined
      const timeline = displayData.timeline as Record<string, unknown>[] | undefined
      const intersectionPoints = displayData.intersection_points as Array<{
        id: string
        name: string
        type: string
        timing: string
        location: string
        content: string
        purpose: string
      }> | undefined

      // v5 T6 交集点格式
      if (intersectionPoints && intersectionPoints.length > 0) {
        return (
          <div style={{ padding: 16 }}>
            <Card size="small" title={`交集点场景 (${intersectionPoints.length})`}>
              <List
                size="small"
                dataSource={intersectionPoints}
                renderItem={(point) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="场景">
                        <Tag color="blue">{point.type}</Tag>
                        {point.name}
                      </Descriptions.Item>
                      <Descriptions.Item label="时间">{point.timing}</Descriptions.Item>
                      <Descriptions.Item label="地点">{point.location}</Descriptions.Item>
                      <Descriptions.Item label="内容">{point.content?.substring(0, 200)}...</Descriptions.Item>
                      <Descriptions.Item label="目的">{point.purpose}</Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            </Card>
          </div>
        )
      }

      // v4 格式
      return (
        <div style={{ padding: 16 }}>
          {quests && quests.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>任务 ({quests.length})</Text>
              <List
                size="small"
                dataSource={quests}
                renderItem={(q) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="名称">
                        <Space>
                          <Tag color="green">{q.id as string}</Tag>
                          <Text strong>{q.name as string}</Text>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="描述">{q.description as string}</Descriptions.Item>
                      <Descriptions.Item label="触发条件">{q.trigger_condition as string}</Descriptions.Item>
                      <Descriptions.Item label="完成条件">{q.completion_condition as string}</Descriptions.Item>
                      {(q.rewards as string[])?.length > 0 && (
                        <Descriptions.Item label="奖励">
                          <Space wrap>
                            {(q.rewards as string[]).map((r, idx) => (
                              <Tag key={idx} color="gold">{r}</Tag>
                            ))}
                          </Space>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </List.Item>
                )}
              />
            </>
          )}
          {timeline && timeline.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>时间线 ({timeline.length})</Text>
              <List
                size="small"
                dataSource={timeline}
                renderItem={(t) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="时间">
                        <Tag color="cyan">{t.trigger_time as string}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="节点">
                        <Text strong>{t.name as string}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="事件">{t.event as string}</Descriptions.Item>
                      {!!t.related_npc && (
                        <Descriptions.Item label="关联NPC">
                          <Tag color="purple">{t.related_npc as string}</Tag>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </List.Item>
                )}
              />
            </>
          )}
        </div>
      )
    }

    case 6: {
      // v4: items/variables, v5 T3: relations (关系网络)
      const items = displayData.items as Record<string, unknown>[] | undefined
      const variables = displayData.variables as Record<string, unknown>[] | undefined
      const relations = displayData.relations as Array<{
        from: string
        to: string
        type: string
        description: string
      }> | undefined

      const getItemTypeColor = (type: string) => {
        switch (type) {
          case 'key_item': return 'gold'
          case 'evidence': return 'purple'
          case 'tool': return 'blue'
          case 'info': return 'default'
          default: return 'default'
        }
      }

      // v5 T3 关系网络格式
      if (relations && relations.length > 0) {
        return (
          <div style={{ padding: 16 }}>
            <Card size="small" title={`关系网络 (${relations.length})`}>
              <List
                size="small"
                dataSource={relations}
                renderItem={(rel) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="关系">
                        <Space>
                          <Tag color="blue">{rel.from}</Tag>
                          <Text>→</Text>
                          <Tag color="green">{rel.to}</Tag>
                          <Tag color="purple">{rel.type}</Tag>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="描述">{rel.description}</Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            </Card>
          </div>
        )
      }

      // v4 格式
      return (
        <div style={{ padding: 16 }}>
          {items && items.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>物品 ({items.length})</Text>
              <List
                size="small"
                dataSource={items}
                renderItem={(item) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="名称">
                        <Space>
                          <Tag color={getItemTypeColor(item.type as string)}>{item.type as string}</Tag>
                          <Text strong>{item.name as string}</Text>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="描述">{item.description as string}</Descriptions.Item>
                      <Descriptions.Item label="获取方式">{item.acquisition as string}</Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            </>
          )}
          {variables && variables.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>变量 ({variables.length})</Text>
              <List
                size="small"
                dataSource={variables}
                renderItem={(v) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="名称">
                        <Space>
                          <Tag color="blue">{v.type as string}</Tag>
                          <Text strong>{v.name as string}</Text>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="初始值">
                        <Text code>{v.default_value as string}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="用途">{v.usage as string}</Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            </>
          )}
        </div>
      )
    }

    case 7: {
      const resources = displayData.resources as Record<string, unknown> | undefined
      const configBlocks = displayData.config_blocks as Record<string, unknown>[] | undefined
      if (!resources) return <Alert message="无资源数据" type="warning" />
      const npcImages = (resources.npc_images as Record<string, unknown>[])?.length || 0
      const sceneImages = (resources.scene_images as Record<string, unknown>[])?.length || 0
      return (
        <div style={{ padding: 16 }}>
          <Space size="large" style={{ marginBottom: 16 }}>
            <Tag color="pink">NPC头像 {npcImages}</Tag>
            <Tag color="cyan">场景背景 {sceneImages}</Tag>
            {configBlocks && <Tag color="green">配置块 {configBlocks.length}</Tag>}
          </Space>

          {/* NPC头像描述 */}
          {npcImages > 0 && (
            <>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>NPC头像描述</Text>
              <List
                size="small"
                dataSource={resources.npc_images as Record<string, unknown>[]}
                renderItem={(img) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="NPC">
                        <Tag color="blue">{img.npc_id as string}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="风格">
                        <Tag color="purple">{img.style as string}</Tag>
                        <Tag>{img.resolution as string}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="提示词">
                        <Paragraph
                          type="secondary"
                          ellipsis={{ rows: 2 }}
                          style={{ margin: 0, fontSize: 12 }}
                        >
                          {img.prompt as string}
                        </Paragraph>
                      </Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            </>
          )}

          {/* 场景背景描述 */}
          {sceneImages > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>场景背景描述</Text>
              <List
                size="small"
                dataSource={resources.scene_images as Record<string, unknown>[]}
                renderItem={(img) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="场景">
                        <Tag color="cyan">{img.scene_id as string}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="风格">{img.style as string}</Descriptions.Item>
                      <Descriptions.Item label="提示词">
                        <Paragraph
                          type="secondary"
                          ellipsis={{ rows: 2 }}
                          style={{ margin: 0, fontSize: 12 }}
                        >
                          {img.prompt as string}
                        </Paragraph>
                      </Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            </>
          )}

          {/* 配置块 */}
          {configBlocks && configBlocks.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>配置块 ({configBlocks.length})</Text>
              <List
                size="small"
                dataSource={configBlocks as Array<{ block_type: string; block_id: string; priority: number }>}
                renderItem={(block) => (
                  <List.Item>
                    <Space wrap size={4}>
                      <Tag color="blue">{block.block_type}</Tag>
                      <Text code>{block.block_id}</Text>
                      <Tag color={block.priority >= 80 ? 'red' : block.priority >= 50 ? 'orange' : 'default'}>
                        优先级: {block.priority}
                      </Tag>
                    </Space>
                  </List.Item>
                )}
              />
            </>
          )}
        </div>
      )
    }

    case 8:
      return (
        <Alert
          message="验证与保存完成"
          description="所有配置已验证并保存到数据库"
          type="success"
          showIcon
          style={{ margin: 16 }}
        />
      )

    default:
      return (
        <pre style={{ padding: 16, maxHeight: 300, overflow: 'auto' }}>
          {JSON.stringify(displayData, null, 2)}
        </pre>
      )
  }
}

export default PhaseProgress
